import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * URL-backed view state for the admin console: the address bar is the source of
 * truth for anything an admin might want to send a colleague — the open tab, a
 * status filter, a search term.
 *
 * These screens used to keep all of it in useState, so every guest list of every
 * event was the same URL (/admin/events/:id/manage) no matter which tab or
 * filter you were looking at, and a refresh threw the view away. These hooks are
 * drop-in replacements for the useState calls that held such values.
 *
 * TWO CONVENTIONS, both of which matter to how the result feels:
 *
 *  1. DEFAULTS STAY OUT OF THE QUERY STRING. Writing a value equal to the
 *     declared default (or "" / null) removes the key instead of spelling it
 *     out, so an untouched screen has a clean URL and a shared link carries only
 *     what was actually changed.
 *  2. REPLACE BY DEFAULT, PUSH ON PURPOSE. Continuous input (a search box) must
 *     not write one history entry per keystroke — that makes Back useless.
 *     Discrete choices (a tab, a filter chip) pass { push: true } so Back undoes
 *     the click, which is what people expect.
 *
 * Multi-key updates go through useUrlPatch() rather than several setters in a
 * row: every write is a functional update over the CURRENT params, so one patch
 * lands as one navigation instead of two writes racing over a stale snapshot.
 *
 * The TypeScript twin of this file lives in vdock-dock (src/lib/useUrlState.ts).
 * Keep the two in step — same keys, same semantics — so a screen that moves
 * between the apps keeps its URLs.
 */

/** Applies a patch to a params snapshot; "" and null delete rather than set. */
function applyPatch(prev, patch) {
  const next = new URLSearchParams(prev);
  for (const [key, value] of Object.entries(patch)) {
    const str = value === null || value === undefined ? "" : String(value);
    if (str === "") next.delete(key);
    else next.set(key, str);
  }
  return next;
}

/**
 * Returns a setter that writes several query-string keys at once. Use it when
 * one interaction changes more than one value, e.g. a new filter that also
 * resets paging: patch({ filter: "going", page: null }, { push: true }).
 */
export function useUrlPatch() {
  const [, setSearchParams] = useSearchParams();
  return useCallback(
    (patch, opts) => {
      setSearchParams((prev) => applyPatch(prev, patch), { replace: opts?.push !== true });
    },
    [setSearchParams],
  );
}

/**
 * One query-string key, shaped like useState. `initial` is both the value seen
 * when the key is absent and the value that removes it.
 *
 *   const [tab, setTab] = useUrlState("tab", "overview", { push: true });
 */
export function useUrlState(key, initial, opts) {
  const [params] = useSearchParams();
  const patch = useUrlPatch();
  const push = opts?.push === true;

  const raw = params.get(key);
  const value = raw === null || raw === "" ? initial : raw;

  // Depends on `push`, not on `opts`: callers pass an object literal, whose
  // identity changes every render and would defeat the memo.
  const set = useCallback(
    (next) => {
      patch({ [key]: next === initial ? null : next }, { push });
    },
    [patch, key, initial, push],
  );

  return [value, set];
}
